import { useCallback, useEffect, useState } from 'react'
import { orpc, toApiUrl } from '@/lib/orpc-client'

export type PushSubscriptionError =
  'permission-denied' | 'not-configured' | 'subscribe-failed' | 'unsubscribe-failed'

export interface PushTestResult {
  configured: boolean
  subscriptions: number
  sent: number
  failed: number
}

interface UsePushSubscriptionReturn {
  isSupported: boolean
  isSubscribed: boolean
  isLoading: boolean
  /** Last subscribe/unsubscribe failure, cleared on the next attempt. */
  error: PushSubscriptionError | null
  subscribe: () => Promise<void>
  unsubscribe: () => Promise<void>
  /** Ask the server to push a test notification to this user's subscriptions. */
  sendTest: () => Promise<PushTestResult>
}

export function usePushSubscription(): UsePushSubscriptionReturn {
  const [isSupported] = useState(
    () => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window
  )
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<PushSubscriptionError | null>(null)

  // Check existing subscription on mount. `ready` only resolves once a worker
  // controls the page, so query registrations explicitly (nothing is
  // registered until the user enables push).
  useEffect(() => {
    if (!isSupported) return
    let cancelled = false
    navigator.serviceWorker
      .getRegistration('/')
      .then((registration) => registration?.pushManager.getSubscription() ?? null)
      .then((sub) => {
        if (!cancelled) setIsSubscribed(!!sub)
      })
      .catch(() => {
        if (!cancelled) setIsSubscribed(false)
      })
    return () => {
      cancelled = true
    }
  }, [isSupported])

  const subscribe = useCallback(async () => {
    if (!isSupported) return
    setIsLoading(true)
    setError(null)

    try {
      // Request notification permission
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        setError('permission-denied')
        return
      }

      // Get VAPID public key from server
      const vapidKey = await fetchVapidPublicKey()
      if (!vapidKey) {
        console.error('[PushSubscription] No VAPID public key available')
        setError('not-configured')
        return
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js')
      await navigator.serviceWorker.ready

      // Create (or reuse) the push subscription. An existing one for another
      // application server key must be dropped first — subscribe() rejects otherwise.
      const existing = await registration.pushManager.getSubscription()
      const applicationServerKey = urlBase64ToUint8Array(vapidKey)
      if (existing && !sameKey(existing.options.applicationServerKey, applicationServerKey)) {
        await existing.unsubscribe()
      }
      const subscription =
        (existing && sameKey(existing.options.applicationServerKey, applicationServerKey)
          ? existing
          : null) ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey as BufferSource,
        }))

      // Extract keys
      const p256dhKey = btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!)))
      const authKey = btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!)))

      // Register with server
      await orpc.notifications.registerPush({
        endpoint: subscription.endpoint,
        p256dhKey,
        authKey,
      })

      setIsSubscribed(true)
    } catch (err) {
      console.error('[PushSubscription] subscribe error:', err)
      setError('subscribe-failed')
    } finally {
      setIsLoading(false)
    }
  }, [isSupported])

  const unsubscribe = useCallback(async () => {
    if (!isSupported) return
    setIsLoading(true)
    setError(null)

    try {
      const registration = await navigator.serviceWorker.getRegistration('/')
      const subscription = await registration?.pushManager.getSubscription()

      if (subscription) {
        const endpoint = subscription.endpoint
        await subscription.unsubscribe()
        await orpc.notifications.unregisterPush({ endpoint })
      }

      setIsSubscribed(false)
    } catch (err) {
      console.error('[PushSubscription] unsubscribe error:', err)
      setError('unsubscribe-failed')
    } finally {
      setIsLoading(false)
    }
  }, [isSupported])

  const sendTest = useCallback(() => orpc.notifications.testPush({}), [])

  return { isSupported, isSubscribed, isLoading, error, subscribe, unsubscribe, sendTest }
}

// --- Helpers ---

async function fetchVapidPublicKey(): Promise<string | null> {
  try {
    const url = toApiUrl('/health')
    const absoluteUrl = /^https?:\/\//i.test(url) ? url : `${window.location.origin}${url}`
    const res = await fetch(absoluteUrl, { credentials: 'include' })
    const data = await res.json()
    return typeof data.vapidPublicKey === 'string' && data.vapidPublicKey
      ? data.vapidPublicKey
      : null
  } catch {
    return null
  }
}

function sameKey(current: ArrayBuffer | null | undefined, wanted: Uint8Array): boolean {
  if (!current) return false
  const a = new Uint8Array(current)
  if (a.length !== wanted.length) return false
  return a.every((byte, i) => byte === wanted[i])
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}
